import { ComponentRef, Injectable } from "@angular/core";
import { Animator, GraphComponent, IAnimation, IGraph, INode, Point, Rect } from "@yfiles/yfiles";
import { NodeService } from "../model-services/node.service";
import { EDGE_TYPE, getInsights, INSIGHTS_FIELD, isModelNode, Linqset, LinqsetEdge, MODEL_TYPE, NODE_SUPERTYPE, NODE_TYPE, ORGANISATION_MODEL_KEY } from "../common/linqset-model";
import { LabelService } from "../model-services/label.service";
import { addInfrastructureNodes, createOnboardingLinqset } from "../layouts/onboarding/onboarding-data";
import { EdgeService } from "../model-services/edge.service";
import { GraphService } from "../canvas/graph.service";
import { Overlay } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { HelpOverlayComponent } from "../help/whats-this/help-overlay.component";
import { LABEL_TYPES } from "../model-services/model-item-enums";
import { InsightsOverlayComponent } from "./insights-overlay.component";
import { LAYOUT_DIRECTION } from "../common/user";
import { LinqsetRepositoryService } from "../model-services/linqset-repository.service";
import { Router } from "@angular/router";
import { SKETCH_URL } from "../app-urls";
import { TemporalService } from "../temporal/temporal.service";

const WAIT_TIME = 4000

@Injectable()
export class ModelIntroductionService {

    constructor(private nodeService:NodeService, private edgeService:EdgeService, private labelService:LabelService, private router:Router,
        private graphService:GraphService, private overlay:Overlay, private linqsetRepoService:LinqsetRepositoryService, private temporalService:TemporalService) {}

    async runAnimation(graphComponent:GraphComponent, linqset:Linqset) {
        graphComponent.graph.clear()

        createOnboardingLinqset(linqset, true)
        const {organisationLinqset} = this.linqsetRepoService
        organisationLinqset.nodes = organisationLinqset.nodes.concat(linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.ROLE || n.superType === NODE_SUPERTYPE.SYSTEM))
        addInfrastructureNodes(organisationLinqset, linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.SYSTEM))

        this.labelService.nodeInsightsField = INSIGHTS_FIELD.NONE

        graphComponent.graph.tag = {orientation:LAYOUT_DIRECTION.LEFT_RIGHT}
        let overlay = this.showOverlayMessage(`Start by capturing your information supply chain - the actions and information they manage`)

        graphComponent.viewPoint = new Point(0, 0)

        const animator = new Animator(graphComponent)
        setTimeout(async ()=> {
            await this.addSupplyChainNodesToGraph(graphComponent, linqset, new Point(100, 200), animator)
            await this.graphService.runLayoutForLinqset(graphComponent, linqset)

            this.removeOverlay(overlay)
            overlay = this.showOverlayMessage(`Next add the roles that perform the actions`)
            setTimeout(async ()=> {
                const position = graphComponent.graph.nodes.first().layout.center
                let edges = linqset.edges.filter(e => e.type === EDGE_TYPE.PERFORM)
                await this.addOrganisationNodesToGraph(graphComponent, linqset, new Point(0, position.y + 150), animator, edges)
                this.labelService.updateLabelsForGraph(graphComponent.graph, false, linqset, INSIGHTS_FIELD.COST)

                await this.graphService.runLayoutForLinqset(graphComponent, linqset)
                setTimeout(async () => {
                    this.removeOverlay(overlay)
                    overlay = this.showOverlayMessage(`Complete the model by adding the systems that are used to do the work and supply the information`)

                    let edges = linqset.edges.filter(e => e.type === EDGE_TYPE.USEBY || e.type === EDGE_TYPE.AUTOMATE || e.type === EDGE_TYPE.SUPPLY)
                    await this.addOrganisationNodesToGraph(graphComponent, linqset, new Point(0, position.y + 150), animator, edges)
                    this.labelService.updateLabelsForGraph(graphComponent.graph, false, linqset, INSIGHTS_FIELD.COST)

                    await this.graphService.runLayoutForLinqset(graphComponent, linqset)
                    this.removeOverlay(overlay)
                    setTimeout(() => {
                        this.removeOverlay(overlay)
                        overlay = this.showOverlayMessage(`Run the model to visualise flow and where queues form over time.`)

                        setTimeout(() => {
                            const captures = graphComponent.graph.nodes.filter(n => n.tag?.node?.type === NODE_TYPE.CAPTURE).map(n => {
                                const i = getInsights(n.tag.node, linqset)
                                return {node:n, itemsPerYear:i?.numberProcessed || 0}
                            })
                            this.temporalService.runAnimation(graphComponent, 30, linqset, captures.toArray(), {})
                            setTimeout(async ()=> {
                                this.temporalService.clear()
                                this.removeOverlay(overlay)
                                const iOverlay = this.showInsightsOverlay("palette", linqset)
                                setTimeout(async ()=> {
                                    this.removeOverlay(iOverlay)
                                    overlay = this.showOverlayMessage(`Create models for different processes and join them together to create an organisation model.`)
                                    this.router.navigateByUrl(SKETCH_URL(linqset.organisationKey, ORGANISATION_MODEL_KEY, MODEL_TYPE.MODELS))
                                    setTimeout(async ()=> {
                                        this.removeOverlay(overlay)
                                        overlay = this.showOverlayMessage(`Assign people to roles and infrastructure to systems to gain insights on workload and carbon emissions.`)
                                        this.router.navigateByUrl(SKETCH_URL(linqset.organisationKey, ORGANISATION_MODEL_KEY, MODEL_TYPE.PEOPLE_AND_SYSTEMS))
                                    }, 5000)
                                }, 30000)
                            }, 15000)
                        }, WAIT_TIME)
                    }, WAIT_TIME)
                }, WAIT_TIME)
            }, WAIT_TIME)

        }, WAIT_TIME)
    }

    addEdgesToGraph(graphComponent:GraphComponent, edges:LinqsetEdge[]) {
        edges.map(e => {
            const from = graphComponent.graph.nodes.find(n => n.tag.node._key === e.sourceKey)
            const to = graphComponent.graph.nodes.find(n => n.tag.node._key === e.targetKey)
            this.edgeService.createEdge(graphComponent.graph, from, to, e)
        })
    }

    async addSupplyChainNodesToGraph(graphComponent:GraphComponent, linqset:Linqset, start:Point, animator: Animator) {
        let x = start.x
        const nodes = linqset.nodes.filter(n => isModelNode(n.superType))
        for (let n of nodes) {
            x += 125

            const iNode = this.nodeService.createNodeAt(linqset, graphComponent.graph, n, n._key, new Point(graphComponent.viewport.centerX, graphComponent.viewport.topLeft.y))
            const animation = IAnimation.createNodeAnimation(graphComponent.graph, iNode, new Rect(x, start.y, 35, 35), '1s')
            await animator.animate(animation)
            this.labelService.addLabelToNode(graphComponent.graph, iNode, n.name, LABEL_TYPES.NODE_NAME)

            const edges = isModelNode(n.superType)?linqset.edges.filter(e => e.targetKey === n._key && (e.type === EDGE_TYPE.CREATE || e.type === EDGE_TYPE.SUPPORT || e.type === EDGE_TYPE.REALISE)):
                linqset.edges.filter(e => e.sourceKey === n._key && (e.type === EDGE_TYPE.PERFORM || e.type === EDGE_TYPE.SUPPLY))

            this.addEdgesToGraph(graphComponent, edges)
        }
    }

    async addOrganisationNodesToGraph(graphComponent:GraphComponent, linqset:Linqset, start:Point, animator: Animator, edges:LinqsetEdge[]) {
        let x = start.x
        for (let e of edges) {
            x += 125

            const n = linqset.getNode(e.sourceKey)

            const iNode = this.nodeService.createNodeAt(linqset, graphComponent.graph, n, e.sourceVisualisationKey, new Point(graphComponent.viewport.centerX, graphComponent.viewport.topLeft.y))
            const animation = IAnimation.createNodeAnimation(graphComponent.graph, iNode, new Rect(x, start.y, 35, 35), '1s')
            await animator.animate(animation)
            this.labelService.addLabelToNode(graphComponent.graph, iNode, n.name, LABEL_TYPES.NODE_NAME)

            const to = graphComponent.graph.nodes.find(n => n.tag.visualisationKey === e.targetVisualisationKey)
            this.edgeService.createEdge(graphComponent.graph, iNode, to, e)
        }
    }

    showOverlayMessage(message:string) {
        const overlayRef = this.overlay.create({
            hasBackdrop: false,
            panelClass: ["overlay-class"],
            width: 900,
            positionStrategy: this.overlay.position().global().centerHorizontally().top('200px')
        });
        const component = new ComponentPortal(HelpOverlayComponent);
        const overlayComponentRef = overlayRef.attach(component);
        overlayComponentRef.instance.message = message
        return overlayComponentRef
    }

    showInsightsOverlay(elementId:string, linqset:Linqset) {
        const element = document.getElementById(elementId)
        if (element) {
            const overlayRef = this.overlay.create({
                hasBackdrop: true,
                panelClass: ["insights-overlay-class"],
                width: 900,
                height: 550,
                positionStrategy: this.overlay.position().global().centerHorizontally().top('150px')
            });
            const component = new ComponentPortal(InsightsOverlayComponent);
            const overlayComponentRef = overlayRef.attach(component);
            overlayComponentRef.instance.linqset = linqset
            return overlayComponentRef
        }
        return undefined
    }

    removeOverlay<C>(overlayComponentRef:ComponentRef<C>) {
        overlayComponentRef.destroy()
    }
}